import React, { useState, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Autocomplete, Grid, Typography, Card } from "@mui/material";
import dayjs from "dayjs";
import { StyledTextField, StyledDatePicker, theme } from "../StyledComponents";
import { durationType } from "../initialData/initialData";
import { formatDurationUnitForDisplay } from "../HistoriesData/durationUtils";
import { FIELD_SET_IDS } from "../config/defaults/fieldLayoutUtils";
import useSectionFieldLayout from "../config/hooks/useSectionFieldLayout";

const FollowUp = ({
  sectionName,
  activeSection,
  data,
  setData,
  setFollowUpError,
  followUpError,
  details,
  setDetails,
  dateForAppointment,
  fieldLayout: fieldLayoutProp,
}) => {
  const fieldLayout = useSectionFieldLayout(
    fieldLayoutProp,
    FIELD_SET_IDS.followUp,
  );
  console.log(details);
  console.log("dateForAppointment", dateForAppointment);
  const [validationErrors, setValidationErrors] = useState(followUpError);
  const [durationInputValue, setDurationInputValue] = useState("");
  const getDurationParts = (durationValue) => {
    const normalized = String(durationValue || "").trim();
    if (!normalized) return { value: "", unit: "", combined: "" };
    const valueMatch = normalized.match(/\d+/);
    const unitMatch = normalized
      .toLowerCase()
      .match(/\b(minutes?|hours?|days?|weeks?|months?|years?)\b/);
    return {
      value: valueMatch ? valueMatch[0] : "",
      unit: unitMatch ? unitMatch[1] : "",
      combined: normalized,
    };
  };

  const applyDurationSelection = (combinedLabel) => {
    let updatedDetails = { ...details };
    if (!combinedLabel || combinedLabel.trim() === "") {
      updatedDetails.duration = "";
      updatedDetails.start_date = null;
    } else {
      const parsed = getDurationParts(combinedLabel);
      const n = parseInt(parsed.value, 10);
      updatedDetails.duration = combinedLabel;
      if (!isNaN(n) && parsed.unit) {
        updatedDetails.start_date = dayjs(dateForAppointment)
          .add(n, parsed.unit)
          .toISOString();
      } else {
        updatedDetails.start_date = null;
      }
    }
    setDetails(updatedDetails);
    setData([updatedDetails]);
    validateFields(updatedDetails);
    if (!combinedLabel) {
      setDurationInputValue("");
    }
  };

  const handleInputChange = (field, value) => {
    let updatedDetails = { ...details, [field]: value };

    setDetails(updatedDetails);
    setData([{ ...updatedDetails }]);
    validateFields(updatedDetails);
  };

  const validateFields = (detail) => {
    const errors = {};

    // if (!detail.duration && detail.service_type) {
    //   errors.duration =
    //     "Follow-up duration is required if any other field is selected.";
    // }

    if (
      fieldLayout.isFieldVisible("service_type", detail) &&
      fieldLayout.isFieldRequired("service_type", detail) &&
      !detail.service_type?.trim()
    ) {
      errors.service_type = fieldLayout.getValidationMessage(
        "service_type",
        "Follow-up Description is a mandatory field.",
      );
    } else if (detail.start_date && !detail.service_type) {
      errors.service_type =
        "Description is required if follow-up duration is selected.";
    }

    if (
      fieldLayout.isFieldVisible("duration", detail) &&
      fieldLayout.isFieldRequired("duration", detail) &&
      !detail.duration
    ) {
      errors.duration = fieldLayout.getValidationMessage(
        "duration",
        "Duration is a mandatory field.",
      );
    } else if (durationInputValue && !detail.duration) {
      errors.duration = "Please select a duration option.";
    } else if (
      fieldLayout.isFieldVisible("duration", detail) &&
      detail.duration &&
      !getDurationParts(detail.duration).value
    ) {
      errors.duration = "Duration must be a number.";
    }

    setValidationErrors(errors);
    setFollowUpError(errors);
  };

  // Calculate Date from Duration
  useEffect(() => {
    const { value, unit } = getDurationParts(details.duration);
    if (value && unit) {
      const newDate = dayjs(dateForAppointment).add(Number(value), unit).toISOString();
      // This section now mounts on load (it is expanded by default), so a restored
      // draft would re-write an unchanged start_date and dirty the draft for nothing.
      if (details.start_date === newDate) return;
      const updatedDetails = { ...details, start_date: newDate };
      setDetails(updatedDetails);
      setData([updatedDetails]);
    }
  }, [details.duration]);

  const getCombinedDurationLabel = (d) =>
    getDurationParts(d.duration).combined;

  return (
    <ThemeProvider theme={theme}>
      {activeSection ? (
        <Card
          sx={{
            borderRadius: "10px",
            boxShadow: "0 4px 4px 0 #C7C7CC inset",
            backgroundColor: "#FFF",
          }}
        >
          <Grid item xs={12}>
            <Typography
              sx={{ marginLeft: 1 }}
              color="textPrimary"
              style={{ marginTop: "1%" }}
            >
              Follow Up
            </Typography>
            <Grid container spacing={2} sx={{ padding: "10px" }}>
              {fieldLayout.isFieldVisible("service_type", details) && (
              <Grid item {...fieldLayout.getFieldGrid("service_type", { xs: 6, sm: 6, md: 6 })}>
                <StyledTextField
                  fullWidth
                  label={fieldLayout.getFieldLabel(
                    "service_type",
                    "Follow-up Description",
                    details,
                  )}
                  value={details.service_type || ""}
                  onChange={(e) =>
                    handleInputChange("service_type", e.target.value)
                  }
                  error={!!validationErrors.service_type}
                  helperText={validationErrors.service_type}
                />
              </Grid>
              )}

              {fieldLayout.isFieldVisible("duration", details) && (
              <Grid item {...fieldLayout.getFieldGrid("duration", { xs: 12, sm: 6, md: 6 })}>
                <Autocomplete
                  freeSolo={false}
                  options={
                    durationInputValue
                      ? durationType.map(
                          (t) =>
                            `${durationInputValue} ${formatDurationUnitForDisplay(t.value)}`
                        )
                      : []
                  }
                  value={getCombinedDurationLabel(details) || null}
                  inputValue={
                    getCombinedDurationLabel(details) || durationInputValue
                  }
                  open={
                    /^\d+$/.test(durationInputValue) &&
                    !details.duration &&
                    durationInputValue.length > 0
                  }
                  onChange={(_, selectedValue) => {
                    if (!selectedValue) {
                      applyDurationSelection("");
                      return;
                    }
                    const numericPart = selectedValue.split(" ")[0] || "";
                    setDurationInputValue(numericPart);
                    applyDurationSelection(selectedValue);
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === "reset") return;
                    if (value === "") {
                      setDurationInputValue("");
                      applyDurationSelection("");
                      return;
                    }
                    const numericPrefixMatch = value.match(/^\d+/);
                    if (numericPrefixMatch) {
                      const numericPart = numericPrefixMatch[0];
                      setDurationInputValue(numericPart);
                      const cleared = {
                        ...details,
                        duration: "",
                        start_date: null,
                      };
                      setDetails(cleared);
                      setData([cleared]);
                      return;
                    }
                    if (/^\d+$/.test(value)) {
                      setDurationInputValue(value);
                      const cleared = {
                        ...details,
                        duration: "",
                        start_date: null,
                      };
                      setDetails(cleared);
                      setData([cleared]);
                    }
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label={fieldLayout.getFieldLabel("duration", "Duration", details)}
                      fullWidth
                      error={!!validationErrors.duration}
                      helperText={validationErrors.duration}
                    />
                  )}
                />
              </Grid>
              )}

              {fieldLayout.isFieldVisible("start_date", details) && (
              <Grid item {...fieldLayout.getFieldGrid("start_date", { xs: 6, sm: 3 })} sx={{ display: "none" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <StyledDatePicker
                    label={fieldLayout.getFieldLabel(
                      "start_date",
                      "Follow-up Date",
                      details,
                    )}
                    format="DD-MM-YYYY"
                    onChange={(date) => {
                      const formattedDate = date
                        ? dayjs(date).toISOString()
                        : null;
                      handleInputChange("start_date", formattedDate);
                    }}
                    minDate={dayjs()}
                    slotProps={{
                      textField: {
                        error: !!validationErrors.start_date,
                        helperText: validationErrors.start_date,
                      },
                      field: { clearable: true },
                    }}
                    value={
                      details?.start_date ? dayjs(details.start_date) : null
                    }
                  />
                </LocalizationProvider>
              </Grid>
              )}
            </Grid>
          </Grid>
        </Card>
      ) : null}
    </ThemeProvider>
  );
};

export default FollowUp;
